/* Simple Interest Calculator Logic — Interactive Math, URL Sync, Charts & CSV Export */

let siDonutChart = null;
let siGrowthChart = null;

function getSiInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    principal: getVal('siPrincipal', 100000),
    rate: getVal('siRate', 8.5),
    years: getVal('siYears', 5),
  };
}

function updateSiUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('principal', params.principal);
    url.searchParams.set('rate', params.rate);
    url.searchParams.set('years', params.years);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseSiUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const principal = searchParams.get('principal');
  const rate = searchParams.get('rate');
  const years = searchParams.get('years');

  return {
    principal: principal !== null ? +principal : null,
    rate: rate !== null ? +rate : null,
    years: years !== null ? +years : null,
  };
}

function calculateSi(params) {
  const { principal, rate, years } = params;

  const annualInterest = (principal * rate) / 100;
  const totalInterest = annualInterest * years;
  const totalAmount = principal + totalInterest;

  const schedule = [];
  let cumInterest = 0;

  for (let yr = 0; yr <= years; yr++) {
    cumInterest = annualInterest * yr;
    schedule.push({
      year: yr,
      principal: Math.round(principal),
      annualInterest: Math.round(annualInterest),
      cumInterest: Math.round(cumInterest),
      totalBalance: Math.round(principal + cumInterest),
    });
  }

  return {
    principal: Math.round(principal),
    annualInterest: Math.round(annualInterest),
    totalInterest: Math.round(totalInterest),
    totalAmount: Math.round(totalAmount),
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('siPrincipalDisplay', fmt(params.principal));
  setTxt('siRateDisplay', `${params.rate}%`);
  setTxt('siYearsDisplay', `${params.years} yrs`);
}

function renderSiResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('siVerdictHeadline', `Total Interest: ${fmt(result.totalInterest)}`);
  setTxt('siVerdictDetail', `A principal of ${fmt(result.principal)} at ${params.rate}% flat simple interest yields ${fmt(result.totalInterest)} over ${params.years} years, for a total maturity of ${fmt(result.totalAmount)}.`);

  setTxt('siTotalInterestValue', fmt(result.totalInterest));
  setTxt('siTotalAmountValue', fmt(result.totalAmount));
  setTxt('siInitialPrincipalValue', fmt(result.principal));
  setTxt('siInterestMetricValue', fmt(result.totalInterest));
  setTxt('siFinalMetricValue', fmt(result.totalAmount));

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.principal, result.totalInterest);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('Simple Interest Chart rendering error:', err);
  }
}

function renderDonutChart(principal, totalInterest) {
  const ctx = document.getElementById('siDonutChart');
  if (!ctx) return;

  if (siDonutChart) siDonutChart.destroy();

  siDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Principal Amount', 'Total Interest'],
      datasets: [
        {
          data: [principal, totalInterest],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent')],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('siGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const totalBalanceData = schedule.map((r) => r.totalBalance);
  const principalData = schedule.map((r) => r.principal);

  if (siGrowthChart) siGrowthChart.destroy();

  siGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Total Accumulated Amount',
          data: totalBalanceData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2.5,
        },
        {
          label: 'Original Principal',
          data: principalData,
          borderColor: cssVar('--chart-buy'),
          borderDash: [5, 5],
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('siScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.slice(1).map((row) => `
    <tr>
      <td>Year ${row.year}</td>
      <td>${fmt(row.principal)}</td>
      <td class="advantage-renter">+${fmt(row.annualInterest)}</td>
      <td class="advantage-buyer">+${fmt(row.cumInterest)}</td>
      <td style="font-weight: 600;">${fmt(row.totalBalance)}</td>
    </tr>
  `).join('');
}

function exportSiCsv() {
  const params = getSiInputs();
  const result = calculateSi(params);
  const headers = ['Year', 'Principal', 'Annual Interest', 'Cumulative Interest', 'Total Maturity Balance'];
  const rows = result.schedule.slice(1).map((r) => [
    r.year, r.principal, r.annualInterest, r.cumInterest, r.totalBalance
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `simple-interest-schedule-${params.years}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applySiCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultPrincipal = 100000;
  let maxPrincipal = 10000000;
  let stepPrincipal = 5000;

  if (curr === 'INR') {
    defaultPrincipal = 100000;
    maxPrincipal = 10000000;
    stepPrincipal = 5000;
  } else if (curr === 'JPY') {
    defaultPrincipal = 1000000;
    maxPrincipal = 100000000;
    stepPrincipal = 50000;
  }

  const pSlider = document.getElementById('siPrincipal');
  if (pSlider) {
    pSlider.max = maxPrincipal;
    pSlider.step = stepPrincipal;
    pSlider.value = overrideParams.principal ?? defaultPrincipal;
  }

  const rSlider = document.getElementById('siRate');
  if (rSlider && overrideParams.rate !== null) {
    rSlider.value = overrideParams.rate;
  }

  const ySlider = document.getElementById('siYears');
  if (ySlider && overrideParams.years !== null) {
    ySlider.value = overrideParams.years;
  }
}

function recalculateSi() {
  const params = getSiInputs();
  updateDisplays(params);
  updateSiUrlParams(params);
  const result = calculateSi(params);
  renderSiResults(result, params);
}

function initSiCalculator() {
  if (!document.getElementById('siPrincipal')) return;

  const urlParams = parseSiUrlParams();

  initCountry((country) => {
    applySiCountryRanges(country, urlParams);
    recalculateSi();
  });

  const inputs = ['siPrincipal', 'siRate', 'siYears'];
  inputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', recalculateSi);
      el.addEventListener('change', recalculateSi);
    }
  });

  const exportBtn = document.getElementById('exportSiCsv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportSiCsv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initSiCalculator();
});
